/**
 * ToasterService - Toast notification system with action buttons
 * Provides temporary popup notifications with optional callbacks
 */

import { Logger } from './Logger';
import { ZIndex } from '../constants/ZIndex';

const logger = Logger.scope('ToasterService');

export type ToastType = 'info' | 'success' | 'warning' | 'error' | 'neutral' | 'primary' | 'secondary' | 'accent';

export interface ToastButton {
  label: string;
  callback: () => void;
  className?: string;
}

export interface ToastOptions {
  message: string;
  type?: ToastType;
  duration?: number; // milliseconds, 0 = no auto-dismiss
  buttons?: ToastButton[];
  dismissible?: boolean;
  opacity?: number; // 0-1, default 1 (fully opaque)
}

export class ToasterService {
  private static readonly CONTAINER_ID = 'toast-container';
  private static toastCount = 0;
  private static container: HTMLElement | null = null;

  /**
   * Initialize the toaster service
   */
  public static init(): void {
    if (this.container) return;

    // Create toast container with z-index above overlays and modals
    // Using explicit inline styles to ensure positioning works in all contexts
    // (including static pages using CDN Tailwind/DaisyUI)
    const container = document.createElement('div');
    container.id = this.CONTAINER_ID;
    container.className = 'toast toast-top toast-end';
    container.style.cssText = `
      position: fixed;
      top: 1rem;
      right: 1rem;
      z-index: ${ZIndex.TOAST};
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.5rem;
      pointer-events: none;
    `;
    document.body.appendChild(container);
    this.container = container;

    logger.debug('Initialized');
  }

  /**
   * Show a toast notification
   */
  public static show(options: ToastOptions): string {
    this.init(); // Ensure container exists

    const {
      message,
      type = 'info',
      duration = 3000,
      buttons = [],
      dismissible = true,
      opacity = 1
    } = options;

    const toastId = `toast-${++this.toastCount}`;

    // Build alert class based on type
    const alertClass = this.getAlertClass(type);
    const inlineStyles = this.getInlineStyles(type);
    const icon = this.getIcon(type);

    // Build buttons HTML
    const buttonsHtml = buttons.length > 0
      ? `<div class="flex gap-2 mt-2">
          ${buttons.map((btn, idx) => `
            <button class="btn btn-sm ${btn.className || 'btn-primary'}" data-toast-btn="${idx}">
              ${btn.label}
            </button>
          `).join('')}
        </div>`
      : '';

    // Build toast HTML with explicit inline styles for cross-context compatibility
    // Uses both DaisyUI classes (for bundled CSS) AND inline styles (for CDN contexts)
    const toastHtml = `
      <div id="${toastId}" class="alert ${alertClass} shadow-lg mb-2 min-w-[300px] max-w-md animate-fade-in relative" style="pointer-events: auto; ${inlineStyles} padding: 1rem; border-radius: 0.5rem; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);">
        ${dismissible ? `
          <button class="btn btn-sm btn-ghost btn-circle absolute top-2 right-2" style="position: absolute; top: 0.5rem; right: 0.5rem; background: transparent; border: none; cursor: pointer; padding: 0.25rem; opacity: 0.7;" data-toast-close>
            <i class="fas fa-times"></i>
          </button>
        ` : ''}
        <div class="flex items-start gap-3 pr-8" style="display: flex; align-items: flex-start; gap: 0.75rem; padding-right: 2rem;">
          ${icon}
          <div class="flex-1" style="flex: 1;">
            <span>${message}</span>
            ${buttonsHtml}
          </div>
        </div>
      </div>
    `;

    // Add to container
    this.container!.insertAdjacentHTML('beforeend', toastHtml);

    const toastElement = document.getElementById(toastId);
    if (!toastElement) return toastId;

    // Apply opacity if specified
    if (opacity < 1) {
      toastElement.style.opacity = String(opacity);
    }

    // Attach button handlers
    buttons.forEach((btn, idx) => {
      const buttonEl = toastElement.querySelector(`[data-toast-btn="${idx}"]`);
      buttonEl?.addEventListener('click', () => {
        btn.callback();
        this.dismiss(toastId);
      });
    });

    // Attach close button handler
    if (dismissible) {
      const closeBtn = toastElement.querySelector('[data-toast-close]');
      closeBtn?.addEventListener('click', () => {
        this.dismiss(toastId);
      });
    }

    // Auto-dismiss if duration is set
    if (duration > 0) {
      setTimeout(() => {
        this.dismiss(toastId);
      }, duration);
    }

    return toastId;
  }

  /**
   * Show an info toast
   */
  public static info(message: string, duration?: number): string {
    return this.show({ message, type: 'info', duration });
  }

  /**
   * Show a success toast
   */
  public static success(message: string, duration?: number): string {
    return this.show({ message, type: 'success', duration });
  }

  /**
   * Show a warning toast
   */
  public static warning(message: string, duration?: number): string {
    return this.show({ message, type: 'warning', duration });
  }

  /**
   * Show an error toast
   */
  public static error(message: string, duration?: number): string {
    return this.show({ message, type: 'error', duration });
  }

  /**
   * Dismiss a toast
   */
  public static dismiss(toastId: string): void {
    const toast = document.getElementById(toastId);
    if (!toast) return;

    // Add fade-out animation
    toast.classList.add('animate-fade-out');
    
    // Remove after animation
    setTimeout(() => {
      toast.remove();
    }, 300);
  }

  /**
   * Dismiss all toasts
   */
  public static dismissAll(): void {
    if (!this.container) return;
    
    const toasts = this.container.querySelectorAll('[id^="toast-"]');
    toasts.forEach(toast => {
      this.dismiss(toast.id);
    });
  }

  /**
   * Get alert class for toast type
   */
  private static getAlertClass(type: ToastType): string {
    const classes: Record<ToastType, string> = {
      info: 'alert-info',
      success: 'alert-success',
      warning: 'alert-warning',
      error: 'alert-error',
      neutral: 'bg-neutral text-neutral-content',
      primary: 'bg-primary text-primary-content',
      secondary: 'bg-secondary text-secondary-content',
      accent: 'bg-accent text-accent-content'
    };
    return classes[type] || 'alert-info';
  }

  /**
   * Get inline styles for toast type (ensures visibility in all CSS contexts)
   * This is needed for static pages using CDN Tailwind/DaisyUI
   */
  private static getInlineStyles(type: ToastType): string {
    const styles: Record<ToastType, string> = {
      info: 'background-color: #3abff8; color: #002b36; border: 1px solid #3abff8;',
      success: 'background-color: #36d399; color: #002b16; border: 1px solid #36d399;',
      warning: 'background-color: #fbbd23; color: #382800; border: 1px solid #fbbd23;',
      error: 'background-color: #f87272; color: #2d0000; border: 1px solid #f87272;',
      neutral: 'background-color: #3d4451; color: #ffffff; border: 1px solid #3d4451;',
      primary: 'background-color: #570df8; color: #ffffff; border: 1px solid #570df8;',
      secondary: 'background-color: #f000b8; color: #ffffff; border: 1px solid #f000b8;',
      accent: 'background-color: #37cdbe; color: #002b2d; border: 1px solid #37cdbe;'
    };
    return styles[type] || styles.info;
  }

  /**
   * Get icon for toast type
   */
  private static getIcon(type: ToastType): string {
    const icons: Record<ToastType, string> = {
      info: '<i class="fas fa-info-circle text-lg"></i>',
      success: '<i class="fas fa-check-circle text-lg"></i>',
      warning: '<i class="fas fa-exclamation-triangle text-lg"></i>',
      error: '<i class="fas fa-times-circle text-lg"></i>',
      neutral: '<i class="fas fa-circle text-lg"></i>',
      primary: '<i class="fas fa-star text-lg"></i>',
      secondary: '<i class="fas fa-bookmark text-lg"></i>',
      accent: '<i class="fas fa-bolt text-lg"></i>'
    };
    return icons[type] || icons.info;
  }

  /**
   * Show a neutral toast
   */
  public static neutral(message: string, duration?: number): string {
    return this.show({ message, type: 'neutral', duration });
  }

  /**
   * Show a primary toast
   */
  public static primary(message: string, duration?: number): string {
    return this.show({ message, type: 'primary', duration });
  }

  /**
   * Show a secondary toast
   */
  public static secondary(message: string, duration?: number): string {
    return this.show({ message, type: 'secondary', duration });
  }

  /**
   * Show an accent toast
   */
  public static accent(message: string, duration?: number): string {
    return this.show({ message, type: 'accent', duration });
  }
}

// Export for global access
declare global {
  interface Window {
    ToasterService: typeof ToasterService;
  }
}

if (typeof window !== 'undefined') {
  window.ToasterService = ToasterService;
}
